import BounceLoader from '@comp/PlusCourseCard/BounceLoader';
import styled from '@emotion/styled';
import Button from '@base/Button/Button';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Divider from '@base/Divider/Divider';
import Image from '@base/Image';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToLite,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import { batchTypes } from '@constants/index';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCheckPartPaymentStatus,
  getIsLoggedIn,
  getJWTToken
} from '@api/selectors/auth';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getIsFetchingUserData } from '@selectors/auth';
import { readCookie } from '@utils/cookies';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { abProxyJWT, abVariations } from '@utils/abHelper';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { fetchPartPaymentStatus } from '@actions/checkout';
import { useDispatchToStore } from '@hooks/reduxHooks';
import useBatchSkuDetails from '@api/hooks/batch/useBatchSkuDetails';
import { useRouter } from 'next/router';
import isEmpty from '@utils/isEmpty';
import { getMinutesDifferenceBetweenDates } from '@utils/dates';
import useOfferTimer from '@api/hooks/batch/useOfferTimer';
import BatchShare from './BatchShare';
import BatchEnroll from './BatchEnroll';
import BatchStorePrice from './BatchStorePrice';
import BatchEnrollmentDetails from './BatchEnrollmentDetails';
import BatchBrochureDownload from './BatchBrochureDownload';
import {
  BATCH_CONTENT_TYPE_MAP,
  getActiveSku,
  getHasStorePurchase,
  getIsStorePath,
  getOfferEndMessage
} from './utils';
import UpdatesButton from './UpdatesButton';

const DisabledLoadingButton = styled(Button)`
  width: 210px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
`;

const StyDivider = styled(Divider)`
  width: 100%;
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin: 0;
  }
`;

const EnrollSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    gap: ${Spacings.SPACING_4B};
  }
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  align-self: end;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    width: 100%;
    margin-top: -${Spacings.SPACING_1B};
    gap: ${Spacings.SPACING_3B};
  }
`;

const MobileWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const MobileButtonsWrapper = styled(MobileWrapper)`
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const OfferBannerMobile = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  width: 100%;
  height: ${Spacings.SPACING_8B};
  position: relative;
  margin-top: 0;
  span {
    margin-left: ${Spacings.SPACING_1B};
  }
`;

const OfferBanner = styled.div`
  width: 100%;
  height: ${Spacings.SPACING_8B};
  position: relative;
  margin-top: ${Spacings.SPACING_6B};
  span {
    margin-left: ${Spacings.SPACING_6};
  }
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const OfferText = styled.div`
  position: absolute;
  font-size: ${Fonts.H6};
  line-height: ${Fonts.H5};
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-yellow-5);
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-top: 0;
    line-height: ${Fonts.H4};
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 40px;
  position: relative;
  flex-shrink: 0;
  ${mediaQuery.uptoMobile} {
    height: 32px;
  }
`;

const CtaWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  & > button {
    flex-grow: 1;
    flex-basis: 0;
  }
`;

const BatchCTASection = ({
  batchUID,
  batch,
  isLoading,
  isSmallHeader = false,
  handlePurchase,
  isDurationBased
}) => {
  const isRecordeBatch = batch.contentType === BATCH_CONTENT_TYPE_MAP.RECORDED;
  const isLiveRecorded =
    batch.contentType === BATCH_CONTENT_TYPE_MAP.LIVE_RECORDED;
  const dispatch = useDispatchToStore();
  const { route } = useRouter();
  const isStoreRoute = getIsStorePath(route);
  const batchGoalUID = batch?.goal?.uid;
  const [showLoadingButton, setShowLoadingButton] = useState(true);
  const isOfflineBatch = batch?.batchType === batchTypes.CENTRE_BATCH;
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(batchGoalUID)
  );

  const { isEnrolled, batchName, isPrimaryCentreBatch } = batch ?? {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const meJwt = useMyInfo(getJWTToken);
  const { accessControl } = useAccessControl(batchGoalUID);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);
  const fetchingUserData = useSelector(getIsFetchingUserData);
  const isSubscribed = useSelector(getIsSubscribedToGoal(batchGoalUID));
  const isLiteUser = useSelector(getIsSubscribedToLite(batchGoalUID));
  const {
    batchSkuDetails,
    mutate: refetchBatchSkuDetails,
    isLoading: batchSkuLoading
  } = useBatchSkuDetails(batchGoalUID, batchUID);

  useEffect(() => {
    if (batchUID && batchGoalUID) {
      refetchBatchSkuDetails();
    }
  }, [dispatch, batchUID, batchGoalUID, refetchBatchSkuDetails, isLoggedIn]);

  const isEnrolAndShareButtonVisible = !(
    isOfflineSubscription &&
    isOfflineBatch &&
    isPrimaryCentreBatch
  );
  const experimentVariation = useExperiments(
    getExperimentVariation('PB_enrolment_restriction')
  );

  // eslint-disable-next-line no-unneeded-ternary
  const isPrimaryBatchEnabled = !isLoggedIn
    ? false
    : experimentVariation && experimentVariation === abVariations.TEST_1;
  // eslint-disable-next-line no-unused-vars
  const [isLoadingExperimentData, setIsLoadingExperimentData] = useState(
    readCookie('accessToken')
  );

  useEffect(() => {
    setShowLoadingButton(
      !!(
        fetchingUserData ||
        isLoading ||
        (readCookie('accessToken') && !isLoggedIn) ||
        !batch.name ||
        batchSkuLoading
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingUserData, isLoading, isLoggedIn, batchSkuLoading]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchABExperimentData(
        'PB_enrolment_restriction',
        isLoggedIn ? meJwt : abProxyJWT
      )
        .then(() => {
          setIsLoadingExperimentData(false);
        })
        .catch(() => {
          setIsLoadingExperimentData(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus) {
      dispatch(fetchPartPaymentStatus(batchGoalUID));
    }
  }, [dispatch, batch?.goal, isLoggedIn, checkPartPaymentStatus, batchGoalUID]);

  const hasStorePurchase = getHasStorePurchase(batchUID, batchSkuDetails);
  const activeStoreSku = getActiveSku(batchSkuDetails);

  const showStoreSku = !!(
    !hasStorePurchase &&
    !isSubscribed &&
    !isLiteUser &&
    isStoreRoute &&
    activeStoreSku &&
    !isEmpty(activeStoreSku)
  );

  useEffect(() => {
    if (hasStorePurchase || (isSubscribed && !isLiteUser)) {
      handlePurchase(true);
    }
  }, [hasStorePurchase, isSubscribed, isLiteUser, handlePurchase]);

  const { time, stopTimer } = useOfferTimer(activeStoreSku?.priceEffectiveTill);
  const showDivider = () => {
    if (!isSmallHeader) {
      return true;
    }
    if (isSmallHeader && !isRecordeBatch && !isLiveRecorded) {
      return true;
    }
    return false;
  };
  const timeUntilOfferEnds =
    getMinutesDifferenceBetweenDates(
      new Date(activeStoreSku?.priceEffectiveTill),
      new Date()
    ) / 60;

  if (!activeStoreSku?.priceEffectiveTill || timeUntilOfferEnds < 0) {
    stopTimer();
  }

  let offerText = getOfferEndMessage(
    activeStoreSku?.priceEffectiveTill,
    isRecordeBatch
  );

  if (timeUntilOfferEnds >= 0 && timeUntilOfferEnds < 24) {
    offerText = time ? `Offer ends in ${time}` : '';
  }

  const { startsAt, endsAt } = batch || {};
  const loadingLabel = <BounceLoader />;
  return (
    <>
      {showDivider() && <StyDivider />}
      <EnrollSection>
        <MobileWrapper>
          <BatchEnrollmentDetails
            startsAt={startsAt}
            endsAt={endsAt}
            enrollmentExpiry={activeStoreSku?.expiresAt}
            showEnrollmentText={showStoreSku}
            isLoading={isLoading}
            isRecordeBatch={isRecordeBatch}
            activeStoreSku={activeStoreSku}
            isEnrolled={isEnrolled}
            isHybrid={isLiveRecorded}
            isStoreRoute={isStoreRoute}
            isSmallHeader={isSmallHeader}
            batchName={batchName}
            isDurationBased={isDurationBased}
          />
        </MobileWrapper>
        {offerText && showStoreSku && (
          <OfferBannerMobile>
            <ImageWrapper>
              <Image
                src="/presubscribed/generic-banner/offer-background.svg"
                layout="fill"
                alt="offer"
                objectFit="fill"
              />
            </ImageWrapper>
            <OfferText>
              <Image
                src="/store/offer-discount.svg"
                height="16px"
                width="16px"
                alt="offer"
              />
              <span>{offerText}</span>
            </OfferText>
          </OfferBannerMobile>
        )}
        <RightWrapper>
          {!hasStorePurchase && (
            <MobileWrapper>
              {showStoreSku && !showLoadingButton && (
                <BatchStorePrice activeStoreSku={activeStoreSku} />
              )}
              {showLoadingButton ? (
                <DisabledLoadingButton
                  variant="disabled"
                  color="black"
                  label={loadingLabel}
                />
              ) : (
                <BatchEnroll
                  batchUID={batchUID}
                  isPrimaryBatchEnabled={isPrimaryBatchEnabled}
                  isEnrolAndShareButtonVisible={isEnrolAndShareButtonVisible}
                  showStoreSku={showStoreSku}
                  activeSku={activeStoreSku}
                />
              )}
            </MobileWrapper>
          )}
          <MobileButtonsWrapper>
            {isEnrolled && <UpdatesButton batchUID={batchUID} isIconButton />}

            {isStoreRoute && (
              <CtaWrapper>
                <BatchBrochureDownload
                  batchUID={batchUID}
                  isIconButton
                  width="50%"
                  isPurchased={hasStorePurchase}
                />
                <BatchShare
                  batchUID={batchUID}
                  isPrimaryBatchEnabled={isPrimaryBatchEnabled}
                  isIconButton
                  hasStorePurchase={hasStorePurchase}
                />
              </CtaWrapper>
            )}
            {!isStoreRoute && (
              <BatchShare
                batchUID={batchUID}
                isPrimaryBatchEnabled={isPrimaryBatchEnabled}
                isIconButton
                hasStorePurchase={hasStorePurchase}
              />
            )}
          </MobileButtonsWrapper>
        </RightWrapper>
      </EnrollSection>
      {showStoreSku && offerText && (
        <OfferBanner>
          <ImageWrapper>
            <Image
              src="/batch-group/batch-offer-banner.svg"
              layout="fill"
              alt="offer"
              objectFit="cover"
            />
          </ImageWrapper>
          <OfferText>
            <Image
              src="/store/offer-discount.svg"
              height="24px"
              width="24px"
              alt="offer"
            />
            <span>{offerText}</span>
          </OfferText>
        </OfferBanner>
      )}
    </>
  );
};

export default BatchCTASection;
